import React, { useCallback, useEffect, useState } from 'react';
import { useField, useForm } from 'react-final-form';
import AxiosHelper from 'services/AxiosHelper';
import { Localizer } from 'services/Localizer';
import { ServerData } from 'services/ServerData';
import Spinner from 'shared/components/Spinner';

/**
 * Button used to resend a confirmation email to a user from a login form. Please note this currently requires the
 * login credential field to be named 'email' or 'userName' depending on if ServerData.USE_EMAIL_AS_USERNAME is true or false.
 * @param props
 */
export default function ReSendAccountConfirmationButton() {
	const [isConfirmationEmailSending, setIsConfirmationEmailSending] = useState(false);
	const [hasSendConfirmationError, setHasSendConfirmationError] = useState(false);
	const [isConfirmationEmailSent, setIsConfirmationEmailSent] = useState(false);
	const loginForm = useForm();
	const resendConfirmationEmailField = useField('reSendAccountConfirmation');

	/**
	 * Sends a confirmation email to a user
	 */
	const sendEmail = async () => {
		const userName = ServerData.USE_EMAIL_AS_USERNAME
			? loginForm.getFieldState('email')?.value
			: loginForm.getFieldState('userName')?.value;

		setIsConfirmationEmailSending(true);

		try {
			const request = {
				userName: userName,
				emailToken: resendConfirmationEmailField.meta.submitError[0],
			};

			// Request email to be sent from the server
			const response = await AxiosHelper.post('/ajax/user/send-account-confirmation-email', request);

			setIsConfirmationEmailSent(true);
			console.debug('Confirmation email successfully sent to: ' + response.data);
		} catch (error) {
			setHasSendConfirmationError(true);
			console.debug('Confirmation email failed to send', error);
		} finally {
			setIsConfirmationEmailSending(false);
		}
	};

	const resetResendButtonState = useCallback(() => {
		loginForm.resetFieldState('reSendAccountConfirmation');
		setIsConfirmationEmailSent(false);
		setHasSendConfirmationError(false);
	}, [loginForm]);

	useEffect(() => {
		if (resendConfirmationEmailField.meta.submitting) {
			resetResendButtonState();
		}
	}, [resendConfirmationEmailField.meta.submitting, loginForm, resetResendButtonState]);

	return resendConfirmationEmailField.meta.submitError && resendConfirmationEmailField.meta.touched ? (
		<button
			color="secondary"
			className="roc-btn"
			type="button"
			onClick={() => {
				sendEmail();
			}}
			disabled={isConfirmationEmailSending || hasSendConfirmationError || isConfirmationEmailSent}
			data-testid="resend-confirmation-email-btn"
		>
			{hasSendConfirmationError ? (
				Localizer('SendAccountConfirmationEmailFailure')
			) : isConfirmationEmailSent ? (
				Localizer('SendAccountConfirmationEmailSuccess')
			) : (
				<>
					{Localizer('ReSendAccountConfirmationEmail')}
					{isConfirmationEmailSending ? <Spinner light /> : null}
				</>
			)}
		</button>
	) : null;
}
